import { AddressComponents } from "components/AddressComponent/AddressComponent";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, FormikValues } from "formik";
import { directorAutofill } from "formValidations/AddFilingDirectorSchema";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  useCreateAutofillMutation,
  useGetAutofillQuery,
  useUpdateAutofillMutation,
} from "store/apis/autofill";
import {
  useAddAPAutoFillMutation,
  useUpdateAPAutoFillMutation,
} from "store/apis/directorsAndOfficers";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { AddressAutofill } from "types/Models/addressAutofill";
import { DirectorAutofill } from "types/Models/directorAutofill";

type AddFilingDirectorProps = {
  isLoading: boolean;
  open: boolean;
  onClose: () => void;
  groupId: string;
  entityId: string;
  directorUuid?: string;
  updateTaskDirector?: (values: {
    persona_autofill: any;
    address: any;
    designation: string | null;
  }) => void;
  onDirectorCreate: (director: DirectorAutofill) => void;
  title: string;
  autofillKey: "DIRECTOR" | "OFFICER";
};

export const AddFilingDirectorModal = ({
  isLoading,
  open,
  onClose,
  groupId,
  entityId,
  directorUuid,
  updateTaskDirector,
  onDirectorCreate,
  title,
  autofillKey,
}: AddFilingDirectorProps) => {
  const addressAutoFillKey = "addresses";
  const personaAutoFillKey = "authorized_persons";
  const { data: addressAutofills = [], isLoading: isAddressAutofillLoading } =
    useGetAutofillQuery(
      {
        groupId,
        autofillKey: addressAutoFillKey,
        entityId,
      },
      { skip: !groupId || !entityId }
    );

  const { data: personaAutofills = [], isLoading: isPersonaAutofillLoading } =
    useGetAutofillQuery(
      {
        groupId,
        autofillKey: personaAutoFillKey,
        entityId,
      },
      { skip: !groupId || !entityId }
    );

  const { data: directorAutofills = [], isLoading: isDirectorAutofillLoading } =
    useGetAutofillQuery(
      {
        groupId,
        autofillKey: autofillKey,
        entityId,
      },
      { skip: !groupId || !entityId }
    );
  const { data: countries } = useGetCountriesDataQuery();
  const [createAutofill, { isLoading: isCreating }] =
    useCreateAutofillMutation();
  const [createOfficerAP] = useAddAPAutoFillMutation();
  const [updateAPAutofill] = useUpdateAPAutoFillMutation();

  const { alertToast, successToast } = useToast();

  const [initialValue, setInitialValue] = useState({
    fullName: "",
    address: "",
    city: "",
    country: "",
    state: "",
    street_address: "",
    street_address_line_2: "",
    zipcode: "",
    designation: "",
  });

  const updateAddressState = (
    address: {
      city: any;
      country: any;
      state: any;
      street_address: any;
      street_address_line_2: string | null;
      zipcode: any;
      uuid: any;
    },
    designation: string
  ) => {
    setInitialValue((initialValue) => ({
      ...initialValue,
      address: address.uuid || "",
      city: address.city || "",
      country: address.country || "",
      state: address.state || "",
      street_address: address.street_address || "",
      street_address_line_2: address.street_address_line_2 || "",
      zipcode: address.zipcode || "",
      designation,
    }));
  };

  useEffect(() => {
    if (directorUuid || "") {
      const directorForUpdate = directorAutofills.find(
        ({ uuid }) => uuid === directorUuid || ""
      );
      if (directorForUpdate) {
        setInitialValue({
          fullName: directorForUpdate.persona_autofill?.uuid || "",
          address: directorForUpdate.address?.uuid || "",
          city: directorForUpdate.address?.city || "",
          country: directorForUpdate.address?.country || "",
          state: directorForUpdate.address?.state || "",
          street_address: directorForUpdate.address?.street_address || "",
          street_address_line_2:
            directorForUpdate.address?.street_address_line_2 || "",
          zipcode: directorForUpdate.address?.zipcode || "",
          designation: directorForUpdate?.designation || "",
        });
      }
    }
  }, [directorAutofills.length, directorUuid || ""]);

  const createNewPersona = async (name: string, designation: string) => {
    try {
      const addedPersona = await createAutofill({
        groupId,
        entityId,
        autofillKey: personaAutoFillKey,
        fields: { first_name: name, last_name: "" },
      }).unwrap();
      setInitialValue((initialValue) => ({
        ...initialValue,
        fullName: addedPersona?.uuid || "",
        designation,
      }));
      successToast({ message: "Authorized person added successfullly!" });
    } catch (e: any) {
      alertToast({ message: e?.response?.data?.error?.message });
    }
  };

  const editDirector = async (values: FormikValues) => {
    try {
      const persona = personaAutofills.find(
        ({ uuid }) => uuid === values.fullName
      );
      const address = addressAutofills.find(
        ({ uuid }) => uuid === values.address
      );

      await updateAPAutofill({
        entityId,
        personaType: autofillKey,
        personaId: directorUuid || "",
        payload: {
          address_autofill_id: values.address,
          persona_autofill_id: values.fullName,
          designation: values.designation,
        },
      }).unwrap();
      updateTaskDirector?.({
        address: address,
        persona_autofill: persona,
        designation: values.designation,
      });
      onClose();
    } catch (e: any) {
      alertToast({ message: e?.response?.data?.error?.message });
    }
  };

  const addDirector = async (values: FormikValues) => {
    try {
      const directorPayload = {
        persona_autofill_id: values.fullName,
        address_autofill_id: values.address,
        designation: values.designation,
      };

      const director = await createOfficerAP({
        entityId,
        personaType: autofillKey,
        payload: directorPayload,
      }).unwrap();
      onDirectorCreate(director);
      successToast({
        message: `${
          autofillKey === "DIRECTOR" ? "Director" : "Officer"
        } added successfullly!`,
      });
      onClose();
    } catch (e: any) {
      alertToast({ message: e?.response?.data?.error?.message });
    }
  };

  const updatePersonaDetails = (
    personaValue: SingleValue<OptionData> | MultiValue<OptionData>,
    validateForm: () => void,
    designation: string
  ) => {
    setInitialValue((initialValue) => ({
      ...initialValue,
      //@ts-ignore
      fullName: personaValue?.value,
      designation: designation,
    }));
    setTimeout(() => {
      validateForm();
    }, 100);
  };

  const updateAddressFields = (
    selectedAddress: AddressAutofill | null,
    validateForm: () => void,
    designation: string
  ) => {
    if (selectedAddress) {
      updateAddressState(selectedAddress, designation);
      setTimeout(() => {
        validateForm();
      }, 100);
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Formik
        initialValues={initialValue}
        onSubmit={directorUuid ? editDirector : addDirector}
        validationSchema={directorAutofill(countries)}
        enableReinitialize
      >
        {({
          isSubmitting,
          values,
          submitForm,
          isValid,
          validateForm,
          setFieldValue,
        }) => {
          const currentPersona = personaAutofills.find(
            ({ uuid }) => uuid === values.fullName
          );

          return (
            <Form className="all:unset">
              <Modal.Content useCustomOverlay>
                <Modal.Header>
                  <Modal.Title>{title}</Modal.Title>
                  <Modal.Close type="button" />
                </Modal.Header>
                <Modal.Body className="t-flex t-flex-col t-gap-5">
                  {isPersonaAutofillLoading ||
                  isAddressAutofillLoading ||
                  isDirectorAutofillLoading ? (
                    <Loader />
                  ) : (
                    <>
                      {autofillKey === "OFFICER" && (
                        <TextInput
                          block
                          label="Designation"
                          name="designation"
                          placeholder="Enter Designation"
                        />
                      )}
                      <Combobox
                        menuPortalTarget={document.body}
                        onChange={(
                          personaValue:
                            | SingleValue<OptionData>
                            | MultiValue<OptionData>
                        ) => {
                          updatePersonaDetails(
                            personaValue,
                            validateForm,
                            values.designation
                          );
                        }}
                        withForm
                        components={{ ClearIndicator: () => null }}
                        name="fullName"
                        label="Full Name"
                        placeholder="Enter full name"
                        options={personaAutofills.map((autofill) => ({
                          label: (
                            <div className="t-text-body-sm t-w-full t-truncate">
                              {autofill.first_name} {autofill.last_name}
                            </div>
                          ),
                          value: autofill.uuid,
                        }))}
                        creatable
                        onCreateOption={(val) =>
                          createNewPersona(val, values.designation)
                        }
                        value={
                          currentPersona?.uuid
                            ? {
                                label: (
                                  <div className="t-text-body-sm t-w-full t-truncate">
                                    {currentPersona?.first_name || ""}{" "}
                                    {currentPersona?.last_name || ""}
                                  </div>
                                ),
                                value: currentPersona?.uuid || "",
                              }
                            : undefined
                        }
                        isLoading={isCreating}
                      />

                      <AddressComponents
                        formPrefix=""
                        entityId={entityId}
                        groupId={groupId}
                        selectedAddressId={values.address}
                        label="Address"
                        onAddressSelect={(address) => {
                          setFieldValue("address", address?.uuid || "");
                          updateAddressFields(
                            address,
                            validateForm,
                            values.designation
                          );
                        }}
                        srollToSave
                        hideClear
                      />
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer className="t-flex t-justify-end t-gap-3">
                  <Button type="button" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    customType="primary"
                    isLoading={isSubmitting || isLoading}
                    disabled={isSubmitting || isLoading || !isValid}
                    onClick={submitForm}
                  >
                    Save
                  </Button>
                </Modal.Footer>
              </Modal.Content>
            </Form>
          );
        }}
      </Formik>
    </Modal.Root>
  );
};
